import {
  CloseOutlined as CloseIcon,
} from "@/components/icons";
import * as React from "react";
import { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";

import { keys } from "@/api/queryKeys";
import type { Me } from "@/api/types";
import { PublishedLegalLinks } from "@/features/legal/LegalLinks";
import { brand } from "@/theme";

type Binding = { keys: string[]; what: string };
type Section = { title: string; bindings: Binding[] };

function sections(t: TFunction, askOn: boolean): Section[] {
  return [
    {
      title: t("shortcuts.section.composer"),
      bindings: [
        { keys: ["Enter"], what: t("shortcuts.send") },
        { keys: ["Shift", "Enter"], what: t("shortcuts.newline") },
        { keys: ["Alt", "Enter"], what: t("shortcuts.newline_alt") },
        { keys: ["↑"], what: t("shortcuts.history_prev") },
        { keys: ["↓"], what: t("shortcuts.history_next") },
        { keys: ["/render"], what: t("shortcuts.render") },
      ],
    },
    {
      title: t("shortcuts.section.attachments"),
      bindings: [
        { keys: [t("shortcuts.key_drag")], what: t("shortcuts.drop_files") },
        { keys: [t("shortcuts.key_click_clip")], what: t("shortcuts.pick_file") },
        { keys: [t("shortcuts.key_click_star")], what: t("shortcuts.mark_primary") },
      ],
    },
    {
      title: t("shortcuts.section.transcript"),
      bindings: [
        { keys: ["↓"], what: t("shortcuts.jump_latest") },
        { keys: [t("shortcuts.key_click_tool")], what: t("shortcuts.toggle_tool") },
        { keys: [t("shortcuts.key_hover_copy")], what: t("shortcuts.copy") },
      ],
    },
    {
      title: t("shortcuts.section.navigation"),
      bindings: [
        { keys: ["?"], what: t("shortcuts.open_help") },
        ...(askOn ? [{ keys: ["Ctrl", "/"], what: t("shortcuts.open_ask") }] : []),
        { keys: ["Esc"], what: t("shortcuts.close_modal") },
      ],
    },
  ];
}

/** Keyboard-shortcut cheatsheet, global ``?`` trigger.
 *
 * Some affordances show their shortcut in context (tool rows show
 * chevrons, the render button has a tooltip), but the composer's keys
 * (Enter, Shift+Enter, ↑/↓ history, ``/render``) are listed only here
 * since FW-30 removed the hint line under the composer. A scannable
 * consolidated list is the fastest way for a returning user to
 * remember the binding they half-remember.
 */
export function KeyboardShortcutsModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  // Mounted at the root, signed out too: read the signed-in user from the
  // cache instead of fetching it. Ctrl+/ exists only where requests are on.
  const me = useQueryClient().getQueryData<Me>(keys.me);
  const askOn = me?.features?.requests_enabled === true;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack
          direction="row"
          sx={{ alignItems: "center", justifyContent: "space-between" }}
        >
          <Box>
            <Typography
              variant="overline"
              sx={{ color: brand.muted, letterSpacing: 1.6 }}
            >
              {t("shortcuts.overline")}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 600, mt: 0.5 }}>
              {t("shortcuts.title")}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            aria-label={t("common.close", "Close")}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack sx={{ gap: 3 }}>
          {sections(t, askOn).map((section) => (
            <Box key={section.title}>
              <Typography
                variant="overline"
                sx={{ color: "text.secondary", letterSpacing: 1.4 }}
              >
                {section.title}
              </Typography>
              <Stack sx={{ gap: 1, mt: 0.75 }}>
                {section.bindings.map((b) => (
                  <Stack
                    key={b.what}
                    direction={{ xs: "column", sm: "row" }}
                    sx={{
                      gap: { xs: 0.5, sm: 2 },
                      alignItems: { sm: "center" },
                    }}
                  >
                    <Stack
                      direction="row"
                      sx={{
                        gap: 0.5,
                        flexShrink: 0,
                        minWidth: { sm: 220 },
                        flexWrap: "wrap",
                      }}
                    >
                      {b.keys.map((k, idx) => (
                        <React.Fragment key={`${k}-${idx}`}>
                          {idx > 0 && (
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary", alignSelf: "center" }}
                            >
                              +
                            </Typography>
                          )}
                          <Kbd label={k} />
                        </React.Fragment>
                      ))}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {b.what}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          ))}
          {/* The legal documents, out of the way but one click from
              every page: this dialog is the app's help entry point on
              every screen size. New tab, so nobody loses their place. */}
          <Box sx={{ borderTop: 1, borderColor: "divider", pt: 2 }}>
            <Typography
              variant="overline"
              sx={{ color: "text.secondary", letterSpacing: 1.4 }}
            >
              {t("legal.section_title")}
            </Typography>
            <PublishedLegalLinks docs={["terms", "privacy", "dpa"]} newTab sx={{ mt: 0.5 }} />
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

function Kbd({ label }: { label: string }): React.JSX.Element {
  return (
    <Box
      component="kbd"
      sx={{
        display: "inline-block",
        px: 0.75,
        py: 0.25,
        borderRadius: 0.75,
        border: 1,
        borderColor: "divider",
        bgcolor: brand.ljusgra,
        fontFamily: brand.fonts.mono,
        fontSize: 12,
        fontWeight: 500,
        lineHeight: 1.4,
        color: "text.primary",
      }}
    >
      {label}
    </Box>
  );
}

/** DOM event the appbar's Help button (and any other UI affordance)
 *  dispatches to open the cheatsheet. Decouples the trigger from
 *  the modal — the appbar doesn't need a reference to the modal's
 *  state, and the modal lives at the app root so ``?`` works
 *  across every route without a provider refactor. */
export const OPEN_SHORTCUTS_EVENT = "repoworks:open-shortcuts";

// Hook + utility + the modal component all co-locate because they
// form one cohesive feature. Splitting them just to satisfy Fast
// Refresh would break the "open the file, see everything" win.
// eslint-disable-next-line react-refresh/only-export-components
export function openKeyboardShortcuts(): void {
  window.dispatchEvent(new Event(OPEN_SHORTCUTS_EVENT));
}

/** Mount at the app root so ``?`` anywhere in the app opens the
 *  cheatsheet. Deliberately ignores the binding when focus is in
 *  a text input / textarea / contenteditable — users typing "?"
 *  into the composer or a search field shouldn't trigger help. */
// eslint-disable-next-line react-refresh/only-export-components
export function useKeyboardShortcutsListener(): {
  open: boolean;
  setOpen: (v: boolean) => void;
} {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Browsers resolve Shift+/ → key "?". Ignore modified variants
      // (Cmd+/ on macOS opens the URL bar in some browsers; Ctrl+/
      // is a common IDE comment toggle) so we don't fight them.
      if (e.key !== "?") return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA") return;
        if (target.isContentEditable) return;
      }
      e.preventDefault();
      setOpen(true);
    };
    const openNow = () => setOpen(true);
    document.addEventListener("keydown", handler);
    window.addEventListener(OPEN_SHORTCUTS_EVENT, openNow);
    return () => {
      document.removeEventListener("keydown", handler);
      window.removeEventListener(OPEN_SHORTCUTS_EVENT, openNow);
    };
  }, []);

  return { open, setOpen };
}
